import { Link, useRouterState, useNavigate } from "@tanstack/react-router";
import type { ReactNode } from "react";
import { useEffect } from "react";
import {
  LayoutDashboard, FileVideo, Users, BarChart3, Download, User, LogOut, History, Menu,
} from "lucide-react";
import { BrandLogo } from "@/projects/components/brand-logo";
import { cn } from "@/projects/lib/utils";
import { getAuth, setAuth } from "@/projects/lib/store";
import { useAuth } from "@/projects/lib/useStore";
import { Button } from "@/projects/components/ui/button";
import { Sheet, SheetContent, SheetTrigger } from "@/projects/components/ui/sheet";
import { toast } from "sonner";

const adminNav = [
  { to: "/admin", label: "Dashboard", icon: LayoutDashboard, exact: true },
  { to: "/admin/submissions", label: "Video Submissions", icon: FileVideo },
  { to: "/admin/teachers", label: "Teacher Management", icon: Users },
  { to: "/admin/reports", label: "Reports", icon: BarChart3 },
  { to: "/admin/exports", label: "Export History", icon: Download },
  { to: "/admin/profile", label: "Profile", icon: User },
];

const teacherNav = [
  { to: "/teacher", label: "Dashboard", icon: LayoutDashboard, exact: true },
  { to: "/teacher/submissions", label: "My Assigned Submissions", icon: FileVideo },
  { to: "/teacher/history", label: "Verification History", icon: History },
  { to: "/teacher/profile", label: "Profile", icon: User },
];

export function DashboardLayout({
  role,
  children,
  title,
  breadcrumb,
}: {
  role: "admin" | "teacher";
  children: ReactNode;
  title: string;
  breadcrumb?: string[];
}) {
  const auth = useAuth();
  const navigate = useNavigate();
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const nav = role === "admin" ? adminNav : teacherNav;

  useEffect(() => {
    const a = getAuth();
    if (!a || a.role !== role) {
      navigate({ to: role === "admin" ? "/login/admin" : "/login/teacher" });
    }
  }, [role, navigate]);

  const isActive = (to: string, exact?: boolean) =>
    exact ? pathname === to : pathname === to || pathname.startsWith(to + "/");

  const handleLogout = () => {
    setAuth(null);
    toast.success("Logged out successfully");
    navigate({ to: "/" });
  };

  const SidebarBody = (
    <div className="flex h-full flex-col">
      <Link to="/" className="flex items-center gap-2 border-b border-sidebar-border px-5 py-4">
        <BrandLogo className="h-9 w-9" />
        <div className="min-w-0">
          <div className="truncate text-guj text-sm font-bold text-sidebar-foreground">રાજનગર જ્ઞાનોત્સવ</div>
          <div className="text-[11px] font-medium uppercase tracking-wider text-muted-foreground">
            {role === "admin" ? "Admin Panel" : "Teacher Panel"}
          </div>
        </div>
      </Link>
      <nav className="flex-1 space-y-1 p-3">
        {nav.map((item) => {
          const active = isActive(item.to, item.exact);
          return (
            <Link
              key={item.to}
              to={item.to}
              className={cn(
                "flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition-colors",
                active
                  ? "bg-sidebar-primary text-sidebar-primary-foreground shadow-soft"
                  : "text-sidebar-foreground hover:bg-sidebar-accent",
              )}
            >
              <item.icon className="h-4 w-4 shrink-0" />
              <span className="truncate">{item.label}</span>
            </Link>
          );
        })}
      </nav>
      <div className="border-t border-sidebar-border p-3">
        <button
          onClick={handleLogout}
          className="flex w-full items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium text-sidebar-foreground hover:bg-sidebar-accent"
        >
          <LogOut className="h-4 w-4" /> Logout
        </button>
      </div>
    </div>
  );

  return (
    <div className="flex min-h-screen w-full bg-muted/30">
      <aside className="sticky top-0 hidden h-screen w-64 shrink-0 border-r border-sidebar-border bg-sidebar lg:block">
        {SidebarBody}
      </aside>
      <div className="flex min-w-0 flex-1 flex-col">
        <header className="sticky top-0 z-30 flex h-16 items-center gap-3 border-b bg-background/80 px-4 backdrop-blur lg:px-8">
          <Sheet>
            <SheetTrigger asChild>
              <Button variant="ghost" size="icon" className="lg:hidden">
                <Menu className="h-5 w-5" />
              </Button>
            </SheetTrigger>
            <SheetContent side="left" className="w-72 bg-sidebar p-0">
              {SidebarBody}
            </SheetContent>
          </Sheet>
          <div className="min-w-0 flex-1">
            {breadcrumb && breadcrumb.length > 0 && (
              <div className="text-xs text-muted-foreground">{breadcrumb.join(" / ")}</div>
            )}
            <h1 className="truncate text-lg font-semibold">{title}</h1>
          </div>
          <div className="hidden items-center gap-3 sm:flex">
            <div className="text-right text-sm">
              <div className="font-medium leading-tight">{auth?.name}</div>
              <div className="text-xs capitalize text-muted-foreground">{auth?.role}</div>
            </div>
            <div className="grid h-9 w-9 place-items-center rounded-full gradient-hero text-sm font-semibold text-white">
              {auth?.name?.[0] ?? "U"}
            </div>
          </div>
        </header>
        <main className="flex-1 p-4 lg:p-8">{children}</main>
      </div>
    </div>
  );
}
